import { TextAreaField } from "@/features/projects/cards/components/TextAreaField";
import {
  type Opportunity,
  OpportunityStatusEnum,
  updateProject,
} from "@repo/api";
import { RadioButtonGroup } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ModalSheet,
  type ModalSheetProps,
  SectionContainer,
  useSettings,
  useToast,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import { type ChangeEvent, useState } from "react";

const progressItems: OpportunityStatusEnum[] = [
  OpportunityStatusEnum.ClosedWon,
  OpportunityStatusEnum.ClosedLost,
  OpportunityStatusEnum.Invalid,
];

type UpdateProjectStatusModalProps = Pick<
  ModalSheetProps,
  "onClose" | "visible"
> & {
  onSave: (status?: Opportunity["status"]) => void;
  opportunity?: Opportunity;
};

export const UpdateProjectStatusModal = ({
  onClose,
  onSave,
  visible,
  opportunity,
}: UpdateProjectStatusModalProps) => {
  const { general } = useSettings();
  const { updateToast } = useToast();

  const [selectedStatus, setSelectedStatus] = useState<
    Opportunity["status"] | undefined
  >(opportunity?.status);
  const [selectedReason, setSelectedReason] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const reasons = selectedStatus
    ? general?.reasons?.project_status_change?.[selectedStatus]
    : [];

  const handleSave = () => {
    if (isSaving) {
      return;
    }

    if (opportunity) {
      setIsSaving(true);
      updateProject(
        {
          id: opportunity?.id,
          status: selectedStatus,
          statusChangeDetails: selectedReason
            ? { reason: selectedReason, description }
            : undefined,
        },
        updateToast
      )
        .then(() => onSave(selectedStatus))
        .finally(() => setIsSaving(false));
    }
  };

  return (
    <ModalSheet
      visible={visible}
      onClose={onClose}
      title={t("action.update_project_status")}
      primaryButtonProps={{
        text: t("action.save"),
        loading: isSaving,
        onClick: handleSave,
      }}
    >
      <SectionContainer>
        <RadioButtonGroup
          name={t("action.select_status")}
          list={progressItems.map((item) => ({
            name: "status-list",
            id: item,
            label: t(`status.${item.toLowerCase()}`),
            value: item,
            defaultChecked: item === opportunity?.status,
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              setSelectedStatus(e.target.value as Opportunity["status"]);
            },
          }))}
        />
        {hasItems(reasons) && (
          <SectionContainer>
            <RadioButtonGroup
              name={t("action.select_reason")}
              list={reasons.map((key) => ({
                name: "reason-list",
                id: key,
                label: t(`status_change_reason.${selectedStatus}_${key}`),
                value: key,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedReason(e.target.value);
                },
              }))}
            />
            <TextAreaField
              id="project-status-change-reason-description"
              label={t("action.describe_reason")}
              value={description}
              editable={true}
              onChange={setDescription}
            />
          </SectionContainer>
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
